import React from 'react';
import { useParams } from 'react-router-dom';

import { Stack, Typography } from '@mui/material';

import {
  SavedRouteAlertCreateInput,
  useCreateSavedRouteAlertMutation,
  useDeleteSavedRouteAlertMutation,
  useGetSavedRouteAlertQuery,
  useUpdateSavedRouteAlertMutation,
} from 'state/emailAlertsSlices';

import { FloatingLoadingSpinner } from 'features/spinner/Spinner';

import { AlertForm } from './AlertForm';
import { NotificationsPanel } from './NotificationsPanel';
import { useNotificationProject } from './useNotificationProject';

export function AlertFormPage() {
  const { alertId } = useParams<{ alertId?: string }>();
  const isEditMode = !!alertId;

  const { projectSlug, navigateTo } = useNotificationProject();

  const {
    data: alert,
    isLoading: isLoadingAlert,
    error: alertError,
  } = useGetSavedRouteAlertQuery(
    { project_slug: projectSlug!, alert_id: alertId! },
    { skip: !projectSlug || !alertId || !isEditMode },
  );

  const [createAlert, { isLoading: isCreating }] =
    useCreateSavedRouteAlertMutation();
  const [updateAlert, { isLoading: isUpdating }] =
    useUpdateSavedRouteAlertMutation();
  const [deleteAlert] = useDeleteSavedRouteAlertMutation();

  const handleSave = (data: SavedRouteAlertCreateInput) => {
    if (!projectSlug) return;

    try {
      if (isEditMode && alertId) {
        updateAlert({
          project_slug: projectSlug,
          alert_id: alertId,
          data,
        });
      } else {
        createAlert({
          project_slug: projectSlug,
          data,
        });
      }
      navigateTo.manage();
    } catch (err) {
      console.error('Failed to save alert:', err);
    }
  };

  const handleCancel = () => {
    navigateTo.manage();
  };
  const handleDelete = () => {
    if (!projectSlug || !alertId) return;
    deleteAlert({
      project_slug: projectSlug,
      alert_id: alertId,
    });
    navigateTo.manage();
  };

  if (isEditMode && isLoadingAlert) {
    return <FloatingLoadingSpinner />;
  }

  if (isEditMode && (alertError || !alert)) {
    return (
      <NotificationsPanel>
        <Stack spacing={3}>
          <Typography variant="h4">Alert Not Found</Typography>
          <Typography variant="body1">
            The alert you're trying to edit doesn't exist or you don't have
            permission to edit it.
          </Typography>
        </Stack>
      </NotificationsPanel>
    );
  }

  return (
    <AlertForm
      alert={isEditMode ? alert : undefined}
      onSave={handleSave}
      onCancel={handleCancel}
      onDelete={handleDelete}
    />
  );
}
